import type { EventItem, EventMediaItem, ScheduleType, TicketTier } from '../types';
import { normalizeOccurrence } from './occurrence';

function coerceFiniteNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Read a tier row price from list/detail API (camelCase or snake_case). */
export function tierPriceFromRaw(t: unknown): number | null {
  if (!t || typeof t !== 'object') return null;
  const o = t as Record<string, unknown>;
  return coerceFiniteNumber(
    o.price ?? o.ticketPrice ?? o.ticket_price ?? o.basePrice ?? o.base_price ?? o.amount
  );
}

/** Minimum strictly positive price across raw tier rows (list endpoint shape). */
export function minPositivePriceFromRawTiers(tiers: unknown[]): number | null {
  let min: number | null = null;
  for (const t of tiers) {
    const p = tierPriceFromRaw(t);
    if (p != null && p > 0) min = min === null ? p : Math.min(min, p);
  }
  return min;
}

function minPositivePriceFromNormalizedTiers(tiers: Pick<TicketTier, 'price'>[]): number | null {
  let min: number | null = null;
  for (const t of tiers) {
    const p =
      typeof t.price === 'number' && Number.isFinite(t.price) ? t.price : coerceFiniteNumber(t.price);
    if (p != null && p > 0) min = min === null ? p : Math.min(min, p);
  }
  return min;
}

/**
 * Use API `minTicketPrice` / `priceFrom` when &gt; 0; otherwise minimum tier price
 * so Experiences cards never show "from ₹0" when tiers have real prices.
 */
export function coalescePriceFromApiAndRawTiers(apiHint: unknown, rawTiers: unknown[]): number {
  const hint = coerceFiniteNumber(apiHint) ?? 0;
  if (hint > 0) return hint;
  return minPositivePriceFromRawTiers(rawTiers) ?? hint;
}

export function coalescePriceFromApiAndTiers(
  apiHint: unknown,
  tiers: Pick<TicketTier, 'price'>[]
): number {
  const hint = coerceFiniteNumber(apiHint) ?? 0;
  if (hint > 0) return hint;
  return minPositivePriceFromNormalizedTiers(tiers) ?? hint;
}

/** Normalize media list from API (camelCase or snake_case). */
export function normalizeMediaItems(raw: any): EventMediaItem[] {
  const arr = raw?.mediaItems ?? raw?.media_items ?? [];
  if (!Array.isArray(arr)) return [];
  return arr.map((m: any) => ({
    id: String(m.id ?? ''),
    eventId: String(m.eventId ?? m.event_id ?? ''),
    mediaUrl: m.mediaUrl ?? m.media_url ?? '',
    mediaType: (m.mediaType ?? m.media_type ?? 'other') as EventMediaItem['mediaType'],
    sortOrder: m.sortOrder ?? m.sort_order ?? 0,
    title: m.title ?? null,
    altText: m.altText ?? m.alt_text ?? null,
    isActive: m.isActive ?? m.is_active ?? true,
    createdAt: m.createdAt ?? m.created_at ?? '',
    updatedAt: m.updatedAt ?? m.updated_at ?? null,
  }));
}

/** Normalize one media row from API JSON. */
export function normalizeMediaRow(m: any): EventMediaItem {
  return normalizeMediaItems({ mediaItems: [m] })[0];
}

export function galleryImageUrlsFromMedia(items: EventMediaItem[]): string[] {
  return items
    .filter((m) => m.mediaType === 'image' && m.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id))
    .map((m) => m.mediaUrl);
}

export function galleryVideoUrlsFromMedia(items: EventMediaItem[]): string[] {
  return items
    .filter((m) => m.mediaType === 'video' && m.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id))
    .map((m) => m.mediaUrl);
}

/**
 * Primary poster: the first image the organiser uploaded to the event's media gallery.
 * There is deliberately no fallback - no banner URL, stock photo or generated art - so
 * events without a gallery image show a neutral placeholder instead of a fake poster.
 */
export function resolveEventCoverImage(mediaItems: EventMediaItem[]): string {
  return galleryImageUrlsFromMedia(mediaItems)[0] || '';
}

/** Map a raw backend event JSON into `EventItem` (list + detail). */
export function mapRawApiEventToEventItem(e: any): EventItem {
  const mediaItems = normalizeMediaItems(e);
  const gImages = galleryImageUrlsFromMedia(mediaItems);
  const gVideos = galleryVideoUrlsFromMedia(mediaItems);
  const paddedImages = [...gImages];
  while (paddedImages.length < 4) paddedImages.push('');
  const extra = e.extraData || {};
  const images = paddedImages.slice(0, 4);
  const videos =
    gVideos.length > 0
      ? [gVideos[0] || '']
      : Array.isArray(extra.videos)
        ? [...extra.videos, ''].slice(0, 1)
        : [''];

  const venueIdRaw = e.venueId ?? e.venue_id ?? e.venue?.id;

  const rawTiers = Array.isArray(e.tiers) ? e.tiers : [];
  const tierCountApi = Number(e.tierCount ?? e.tier_count ?? 0);
  const tierCount = tierCountApi > 0 ? tierCountApi : rawTiers.length;

  const bookingStartDate =
    e.bookingStartDate ??
    e.booking_start_date ??
    extra.bookingStartDate ??
    extra.booking_start_date;
  const bookingEndDate =
    e.bookingEndDate ?? e.booking_end_date ?? extra.bookingEndDate ?? extra.booking_end_date;

  return {
    ...e,
    ...extra,
    mediaItems,
    id: e.id.toString(),
    venueId: venueIdRaw != null ? venueIdRaw : undefined,
    image: resolveEventCoverImage(mediaItems),
    city: e.venue?.city || extra.city || 'Mumbai',
    venue: e.venue?.name || extra.venue || 'TBA',
    /** Venue row address (API); legacy events may still have extra_data.fullAddress */
    fullAddress: e.venue?.address ?? extra.fullAddress ?? '',
    // `eventDate` is a UTC ISO string; slicing it raw showed the UTC time (e.g.
    // 20:47 IST stored as 15:17Z rendered as "15:17"). Derive both parts in IST
    // so the displayed date/time match what the organizer entered.
    date: e.eventDate
      ? (() => {
          const d = new Date(e.eventDate);
          return Number.isNaN(d.getTime())
            ? e.eventDate.split('T')[0]
            : d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        })()
      : extra.date || '',
    time: e.eventDate
      ? (() => {
          const d = new Date(e.eventDate);
          return Number.isNaN(d.getTime())
            ? e.eventDate.split('T')[1]?.substring(0, 5) || ''
            : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
        })()
      : extra.time || '',
    featured: extra.featured !== undefined ? extra.featured : true,
    priceFrom: coalescePriceFromApiAndRawTiers(
      e.minTicketPrice ?? e.min_ticket_price ?? e.priceFrom ?? extra.priceFrom,
      rawTiers
    ),
    availableTickets: e.availableSeats ?? e.availableTickets ?? extra.availableTickets ?? 0,
    scheduleType: (e.scheduleType ?? e.schedule_type ?? 'single') as ScheduleType,
    occurrenceCount: Number(e.occurrenceCount ?? e.occurrence_count ?? 1),
    occurrences: Array.isArray(e.occurrences) ? e.occurrences.map(normalizeOccurrence) : undefined,
    hasPass: Boolean(e.hasPass ?? e.has_pass),
    hasActivities: Boolean(e.hasActivities ?? e.has_activities),
    eventEndDate: e.eventEndDate ?? e.event_end_date ?? null,
    tierCount,
    tiers: rawTiers,
    category: e.category || 'concert',
    images,
    videos,
    bookingStartDate: bookingStartDate || undefined,
    bookingEndDate: bookingEndDate || undefined,
  };
}
